import { Dialog, DialogTitle, DialogContent, Box, IconButton, Tooltip, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { ICON_SM } from '../../common/theme'
import BidProjectList from '../../comps/BidProjectList'
import { useAppActions, useAppProject } from '../../AppContext'

interface BidProjectPickerModalProps {
  open: boolean
  onClose: () => void
  // Optional extra-side-effect callback fired after the global currentProject
  // is updated - e.g. RBT uses this to clear the question selection. The
  // global update + URL sync happens regardless.
  onSelectProject?: (p: any) => void
  title?: string
}

// Modal that wraps the shared BidProjectList for quick project switching
// from anywhere in the app. Selecting a row writes through to
// AppContext.currentProject (the single source of truth) + syncs the bid
// URL so the surfaces all stay aligned, and clears currentQuestion when the
// project actually changes.
export default function BidProjectPickerModal({
  open, onClose, onSelectProject, title = 'Select a Bid',
}: BidProjectPickerModalProps) {
  console.log('BidProjectPickerModal render')
  const { currentProject } = useAppProject()
  const { setCurrentProject, setCurrentQuestion } = useAppActions()

  const handlePick = (p: any) => {
    const projectChanged = currentProject?.projectsha !== p?.projectsha
    // AppContext.currentProject is the single source of truth across
    // BidManager, Response Builder, etc. The URL no longer encodes the
    // selected project (it became fragile when switching across tabs).
    setCurrentProject(p)
    // Clear question selection when the project changes - the new project's
    // questions won't have the old one's sha so leaving it stale just shows
    // an empty pane until the picker is touched.
    if (projectChanged) setCurrentQuestion(null)
    onSelectProject?.(p)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '70vh' } }}
    >
      <DialogTitle sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', alignItems: 'center' }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem', flex: 1 }}>
          {title}
        </Typography>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
            <CloseIcon sx={{ fontSize: ICON_SM }} />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0, minWidth: 0, '& > *': { flex: 1, minWidth: 0 } }}>
          <BidProjectList
            onSelectProject={handlePick}
            showAddBid={false}
            showDelete={false}
            title="Bids"
            testIdPrefix="bidpicker"
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
